import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { HugeiconsIcon } from '@hugeicons/react'
import { Delete02Icon } from '@hugeicons-pro/core-bulk-rounded'
import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { Box, Button, CircularProgress, Dialog, DialogContent, IconButton, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { useSnackbar } from 'notistack'
import PropTypes from 'prop-types'

import { useAddOrderItemsFromFilesMutation } from '@/app/services/orderItems'
import { useCreateOrderMutation } from '@/app/services/orders'
import { selectContactId } from '@/app/slices/contactsSlice'
import { selectCustomerId } from '@/app/slices/customerSlice'
import { selectGuestUser, selectIsGuestLoggedIn } from '@/app/slices/guestUserSlice'
import { setExpandedItemsIds } from '@/app/slices/orderItemsSlice'
import { selectOrganisationId } from '@/app/slices/organisationSlice'
import UploadFilesCard from '@/common/components/AddItemCards/UploadFilesCard'
import FileRequirementsList from '@/common/components/FileRequirementsList'
import TbxShadowScroll from '@/common/components/TbxShadowScroll'
import { useMutationErrorSnackbar } from '@/common/hooks/useMutationErrorSnackbar'
import { ORDER_USER_TYPE } from '@/common/utils/Constants/Constants'

const classes = {
    dialogRoot: {
        '& .MuiDialog-paper': {
            borderRadius: 1,
        },
    },
    dialogContent: {
        display: 'flex',
        flexDirection: 'row',
        gap: '8px',
        padding: 3,
    },
    dropzoneContainer: {
        width: '320px',
        flex: '1 0 320px',
        display: 'flex',
        flexDirection: 'row',
        gap: 1,
    },
    fileRequirementsContainer: {
        width: '350px',
        height: '360px',
        flex: '1 0 350px',
    },
    fileListContainer: {
        width: '350px',
        height: '360px',
        overflow: 'hidden',
        flex: '1 0 350px',
        paddingLeft: 2,
    },
    fileListWrapper: {
        marginBottom: 1.5,
    },
    filesToUpload: {
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        marginBottom: 1.5,
    },
    fileItem: {
        px: 1,
        py: 0.5,
        gap: 1,
        backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.05),
        borderRadius: 0.5,
    },
    smallText: {
        fontSize: '0.75rem',
    },
}

const AddOrderItemsDialog = ({ onClose, open }) => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { t } = useTranslation()
    const { enqueueSnackbar } = useSnackbar()
    const { showMutationError } = useMutationErrorSnackbar()
    const { isAuthenticated } = useKindeAuth()
    const { orderId } = useParams()

    const organisationId = useSelector(selectOrganisationId)
    const currentCustomerId = useSelector(selectCustomerId)
    const currentContactId = useSelector(selectContactId)
    const isGuestLoggedIn = useSelector(selectIsGuestLoggedIn)
    const guestUser = useSelector(selectGuestUser)

    const [createOrder, { isLoading: isCreating, reset: resetCreateOrder }] = useCreateOrderMutation()
    const [uploadOrderItems, { isLoading: isUploading, reset: resetUploadOrderItems }] =
        useAddOrderItemsFromFilesMutation()

    const uploadRef = useRef(null)

    const [filesToUpload, setFilesToUpload] = useState([])

    const handleAddedFiles = (updatedFiles) => {
        setFilesToUpload(updatedFiles)
    }

    const handleFileRemoval = (fileName) => {
        uploadRef.current.removeFile(fileName)
    }

    const handleClose = () => {
        onClose()
        uploadRef.current.handleCleanUploadList()
    }

    const handleFilesUpload = async (filesToUpload) => {
        if (filesToUpload.length) {
            let newOrderId = null
            const userType = isAuthenticated ? ORDER_USER_TYPE.Customer : ORDER_USER_TYPE.Guest
            const contactId = isAuthenticated ? currentContactId : isGuestLoggedIn ? guestUser.contactId : null
            const customerId = isAuthenticated ? currentCustomerId : isGuestLoggedIn ? guestUser.customerId : null

            if (!orderId) {
                try {
                    const createOrderResponse = await createOrder({
                        organisationId,
                        payload: {
                            organisationId,
                            quoteUserType: userType,
                            customerContactId: contactId,
                            customerId: customerId,
                        },
                    }).unwrap()
                    newOrderId = createOrderResponse.id
                } catch (error) {
                    showMutationError(error, t('Failed to create order'))

                    handleClose()
                    resetUploadOrderItems()
                    resetCreateOrder()
                    return
                }
            }

            try {
                const formData = new FormData()
                filesToUpload.forEach((el) => formData.append('files', el, el.name))
                const uploadResponse = await uploadOrderItems({
                    organisationId,
                    orderId: newOrderId || orderId,
                    drawingFiles: formData,
                }).unwrap()

                const orderItemsIds = uploadResponse.quoteItems.map((item) => item.id)
                dispatch(setExpandedItemsIds(orderItemsIds))
                enqueueSnackbar(`${filesToUpload.length} file(s) ${t('added to order')}`, { variant: 'success' })
            } catch (error) {
                let fileUploadErrorMessage = t('Failed to upload order items')
                if (error.response?.status === 408 || error.response?.status === 504) {
                    fileUploadErrorMessage = t(
                        'The upload timed out. Please try again. If the problem persists, please contact our support team.'
                    )
                }
                showMutationError(error, t(fileUploadErrorMessage))
            } finally {
                handleClose()
                resetUploadOrderItems()
                resetCreateOrder()
            }

            if (newOrderId) {
                navigate(`orders/${newOrderId}`)
            }
        }
    }

    return (
        <Dialog
            disableEscapeKeyDown={isUploading}
            maxWidth="md"
            open={open}
            sx={classes.dialogRoot}
            onClose={isUploading ? undefined : handleClose}
        >
            <DialogContent sx={classes.dialogContent}>
                <Box sx={classes.dropzoneContainer}>
                    <UploadFilesCard
                        isUploading={isUploading}
                        ref={uploadRef}
                        onFilesChange={handleAddedFiles}
                    />
                </Box>

                {!filesToUpload.length ? (
                    <Box sx={classes.fileRequirementsContainer}>
                        <FileRequirementsList />
                    </Box>
                ) : (
                    <Box
                        sx={[
                            {
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                            },
                            classes.fileListContainer,
                        ]}
                    >
                        <TbxShadowScroll
                            scrollContainerSx={classes.filesToUpload}
                            wrapperSx={classes.fileListWrapper}
                        >
                            {filesToUpload.map((file) => (
                                <Box
                                    key={file.name}
                                    sx={[
                                        {
                                            alignItems: 'center',
                                            display: 'flex',
                                            flexDirection: 'row',
                                            justifyContent: 'space-between',
                                        },
                                        classes.fileItem,
                                    ]}
                                >
                                    <Typography variant="body2">{file.name}</Typography>
                                    <IconButton
                                        color="secondary"
                                        size="small"
                                        onClick={() => handleFileRemoval(file)}
                                    >
                                        <HugeiconsIcon
                                            icon={Delete02Icon}
                                            size={18}
                                        />
                                    </IconButton>
                                </Box>
                            ))}
                        </TbxShadowScroll>

                        <Button
                            color="secondary"
                            disabled={isUploading}
                            startIcon={isUploading ? <CircularProgress size={20} /> : null}
                            variant="contained"
                            onClick={() => handleFilesUpload(filesToUpload)}
                        >
                            {isUploading || isCreating ? t('Uploading files...') : t('Upload files')}
                        </Button>
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    )
}

AddOrderItemsDialog.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
}

export default AddOrderItemsDialog
